import { useEffect, useRef, useState } from 'react'

/**
 * Split-view app-shell helper: measures the element's distance from the top of the document and
 * returns a height that fills exactly from there to the viewport bottom (minus a small gap), so the
 * element becomes an independently-scrollable panel (with `overflow: auto`) instead of growing the
 * page. Height is `undefined` until measured (SSR / first paint) — callers fall back to `auto`,
 * which harmlessly behaves like the old page-scroll until it resolves.
 *
 * `bottomGap` also absorbs any fixed footer that overlays the viewport bottom: the split view's
 * combined selection card is a viewport-fixed footer (out of flow), so callers pass its height plus
 * a small visual gap here to stop the panel at the card's top instead of scrolling behind it.
 *
 * Re-measures on window resize AND whenever `document.body`'s size changes (via ResizeObserver) —
 * the latter catches content ABOVE this element (banners, coupon sections, etc.) loading/resizing
 * asynchronously after mount, which shifts this element's top but isn't a window resize. Without it,
 * a stale top offset desyncs sibling panels that fill height independently (e.g. the filters sidebar
 * vs. the results column), producing page scroll + empty space instead of two matching panels.
 */
export const useViewportFillHeight = <T extends HTMLElement = HTMLDivElement>(bottomGap = 16) => {
    const ref = useRef<T>(null)
    const [height, setHeight] = useState<string>()

    useEffect(() => {
        const measure = () => {
            const el = ref.current
            if (!el) return
            // Natural top relative to the document (scroll-independent), so the panel fills from there
            // to the viewport bottom regardless of current scroll position.
            const top = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY))
            setHeight(`calc(100vh - ${top + bottomGap}px)`)
        }
        measure()
        window.addEventListener('resize', measure)
        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(document.body)
        return () => {
            window.removeEventListener('resize', measure)
            resizeObserver.disconnect()
        }
    }, [bottomGap])

    return { ref, height }
}

export default useViewportFillHeight
